import { forwardRef, type FocusEvent, type TextareaHTMLAttributes } from 'react';

import { Field, type FieldWidth } from '../Field/Field';
import styles from './Textarea.module.css';

/**
 * Textarea — Input's multi-line sibling, and the same contract.
 *
 * It exists for the reason Field exists: fourteen dialogs had each written
 * their own `<label>` + `<textarea>` pair, and the copies had drifted apart on
 * ground, border, padding and line-height, with none of them carrying the
 * hint / error / required chrome that every single-line field gets for free.
 * A "notes" box that looks nothing like the "mobile" box above it reads as a
 * rendering fault, which is exactly the report that produced this file.
 *
 * The three rules Input encodes hold here unchanged:
 *
 * 1. **Validate on blur, never on keystroke** — `onValidate` fires from
 *    `onBlur` and nowhere else.
 * 2. **Required is marked; optional is never marked.**
 * 3. **Width signals content length** — but see the default below.
 *
 * `width` DEFAULTS TO `'fill'` here, where Input deliberately has no default.
 * That is not an inconsistency: Input's rule is "a full-width input for a
 * SHORT value is a bug", and a control the agent is invited to write
 * paragraphs into is the handoff's own "free text -> fill" case. A caller can
 * still pass something narrower.
 */

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'required' | 'className' | 'id'> {
  /** Visible label, rendered above the field. */
  label: string;
  /** Handoff, Forms 2. Free text fills, so that is the default. */
  width?: FieldWidth;
  /** Message describing the FIX. Present means invalid. */
  error?: string;
  /** Standing help text under the field. */
  hint?: string;
  required?: boolean;
  /** Called on blur with the field's current value. Never called while typing. */
  onValidate?: (value: string) => void;
  id?: string;
  /** Applied to the field wrapper, not to the textarea. */
  className?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    width = 'fill',
    error,
    hint,
    required = false,
    onValidate,
    id,
    className,
    disabled = false,
    onBlur,
    rows = 4,
    ...rest
  },
  ref,
) {
  function handleBlur(event: FocusEvent<HTMLTextAreaElement>) {
    onValidate?.(event.target.value);
    onBlur?.(event);
  }

  return (
    <Field
      label={label}
      width={width}
      required={required}
      error={error}
      hint={hint}
      disabled={disabled}
      id={id}
      className={className}
    >
      {({ widthClassName, className: controlClassName, ...control }) => (
        <textarea
          {...rest}
          {...control}
          ref={ref}
          rows={rows}
          className={[controlClassName, widthClassName, styles['textarea']]
            .filter(Boolean)
            .join(' ')}
          onBlur={handleBlur}
        />
      )}
    </Field>
  );
});
